import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { Link, useParams, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import {
  ArrowLeft,
  Save,
  X,
  Plus,
  Trash,
  Image,
  Film,
  Edit,
  Eye,
  Upload,
  Package,
  Crown,
  Zap,
  FileText
} from "lucide-react";

import {
  getAccessory,
  addAccessory,
  updateAccessory,
  clearAccessory,
  uploadAccessoryImage,
  deleteAccessoryImage
} from "../../actions/accessory";

const initialState = {
  name: "",
  brand: "",
  description: "",
  images: [],
  productType: "",
};

const Accessory = ({
  getAccessory,
  addAccessory,
  updateAccessory,
  clearAccessory,
  uploadAccessoryImage,
  deleteAccessoryImage,
  accessory,
  loading,
  newAccessoryCreated,
  assetPath
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialState);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const isNewAccessory = id === "new";

  // Clear accessory data when component mounts
  useEffect(() => {
    // Always clear accessory state when component mounts
    clearAccessory();
    
    // If it's not a new accessory, fetch the data
    if (!isNewAccessory) {
      getAccessory(id);
    } else {
      // For new accessory, reset form to initial state
      setFormData(initialState);
    }
    
    // Cleanup function to clear accessory when component unmounts
    return () => {
      clearAccessory();
    };
  }, [id, isNewAccessory, getAccessory, clearAccessory]);

  // Handle navigation after successful creation
  useEffect(() => {
    if (isNewAccessory && accessory && accessory._id) {
      // Small delay to ensure state is properly updated
      setTimeout(() => {
        navigate(`/inventory/v1.2/accessories/${accessory._id}`);
      }, 100);
    }
  }, [isNewAccessory, accessory, navigate]);
  
  // Separate useEffect to handle data updates
  useEffect(() => {
    if (!isNewAccessory && accessory && accessory._id === id) {
      setFormData({
        name: accessory.name || '',
        brand: accessory.brand || '',
        description: accessory.description || '',
        images: Array.isArray(accessory.images) ? accessory.images : [],
        productType: accessory.productType || 'accessory'
      });
    }
  }, [accessory, id, isNewAccessory]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile) return;

    setUploadingFile(true);
    try {
        // parse id and file
      const imageUrl = await uploadAccessoryImage(id, selectedFile);
      setFormData({
        ...formData,
        images: [...formData.images, imageUrl]
      });
      setSelectedFile(null);
      setPreviewImage(null);
    } catch (error) {
      // Handle upload error silently
    } finally {
      setUploadingFile(false);
    }
  };

  const removeImage = async (index) => {
    try {
      // Get the image name from the path
      const imageName = formData.images[index];
      
      if (!isNewAccessory && id) {
        setUploadingFile(true);
        // Call the API to delete the image
        await deleteAccessoryImage(id, imageName);
        // The state will be updated by the reducer after the API call
      } else {
        // For new accessories or if there's an issue, just update the local state
        const updatedImages = formData.images.filter((_, i) => i !== index);
        setFormData({
          ...formData,
          images: updatedImages
        });
      }
    } catch (error) {
      // Handle delete error silently
    } finally {
      setUploadingFile(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    
    if (isNewAccessory) {
      addAccessory(formData);
    } else {
      updateAccessory(id, formData);
    }
  };

  const productTypes = [
    "Accessory"
  ];

  return (
    <Fragment>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap" 
                 style={{ 
                   background: 'linear-gradient(to right, #f8f9fa, #e9ecef)', 
                   padding: '1.5rem', 
                   borderRadius: '0.75rem',
                   boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
                 }}>
              <div className="d-flex align-items-center">
                <Link 
                  to="/inventory/v1.2/accessories"
                  className="btn btn-light me-3 rounded-circle shadow-sm"
                  style={{ width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <ArrowLeft size={20} />
                </Link>
                <div>
                  <h1 className="h3 mb-2 fw-bold" style={{ color: '#2c3e50' }}>
                    {isNewAccessory ? "Add New Accessory" : "Edit Accessory"}
                  </h1>
                  <p className="text-muted mb-0">
                    {isNewAccessory 
                      ? "Create a new accessory for your inventory" 
                      : "Update accessory information and details"
                    }
                  </p>
                </div>
              </div>
              <button
                type="submit"
                form="accessoryForm"
                className="btn btn-primary btn-lg shadow-sm d-flex align-items-center"
                disabled={loading}
                style={{ 
                  background: 'linear-gradient(to right, #4a6cf7, #3b5de7)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {loading ? (
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <Save size={20} className="me-2" />
                )}
                {isNewAccessory ? "Create Accessory" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="row">
          <div className="col-lg-8">
            <div className="card shadow-sm border-0" style={{ borderRadius: '0.75rem', overflow: 'hidden' }}>
              <div className="card-header bg-white border-bottom" style={{ background: 'linear-gradient(to right, #f8f9fa, #ffffff)' }}>
                <h5 className="card-title mb-0 d-flex align-items-center">
                  <div className="bg-primary p-2 rounded-circle me-2 d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                    <Edit size={18} className="text-white" />
                  </div>
                  <span style={{ color: '#2c3e50' }}>Accessory Information</span>
                </h5>
              </div>
              <div className="card-body">
                {loading && !isNewAccessory ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading accessory data...</p>
                  </div>
                ) : (
                  <form id="accessoryForm" onSubmit={onSubmit}>
                    <div className="row g-4">
                      {/* Name */}
                      <div className="col-md-6">
                        <label htmlFor="name" className="form-label fw-bold text-secondary">
                          Accessory Name <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <Package size={16} className="text-primary" />
                          </span>
                          <input
                            type="text"
                            className="form-control border-start-0"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Enter accessory name"
                            style={{ borderRadius: '0 0.375rem 0.375rem 0' }}
                          />
                        </div>
                      </div>

                      {/* Brand */}
                      <div className="col-md-6">
                        <label htmlFor="brand" className="form-label fw-bold text-secondary">
                          Brand <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <Crown size={16} className="text-primary" />
                          </span>
                          <input
                            type="text"
                            className="form-control border-start-0"
                            id="brand"
                            name="brand"
                            value={formData.brand}
                            onChange={handleChange}
                            required
                            placeholder="Enter brand name"
                            style={{ borderRadius: '0 0.375rem 0.375rem 0' }}
                          />
                        </div>
                      </div>

                      {/* Product Type */}
                      <div className="col-md-6">
                        <label htmlFor="productType" className="form-label fw-bold text-secondary">
                          Product Type <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <Zap size={16} className="text-primary" />
                          </span>
                          <select
                            className="form-select border-start-0"
                            id="productType"
                            name="productType"
                            value={formData.productType}
                            onChange={handleChange}
                            required
                            style={{ borderRadius: '0 0.375rem 0.375rem 0' }}
                          >
                            <option value="">Select product type</option>
                            {productTypes.map((type) => (
                              <option key={type} value={type.toLowerCase()}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="col-12">
                        <label htmlFor="description" className="form-label fw-bold text-secondary">
                          Description
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <FileText size={16} className="text-primary" />
                          </span>
                          <textarea
                            className="form-control border-start-0"
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="4"
                            placeholder="Enter accessory description"
                            style={{ borderRadius: '0 0.375rem 0.375rem 0' }}
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Images Section */}
          <div className="col-lg-4">
            <div className="card shadow-sm border-0" style={{ borderRadius: '0.75rem', overflow: 'hidden' }}>
              <div className="card-header bg-white border-bottom" style={{ background: 'linear-gradient(to right, #f8f9fa, #ffffff)' }}>
                <h5 className="card-title mb-0 d-flex align-items-center">
                  <div className="bg-info p-2 rounded-circle me-2 d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                    <Image size={18} className="text-white" />
                  </div>
                  <span style={{ color: '#2c3e50' }}>Media Gallery</span>
                </h5>
              </div>
              <div className="card-body">
                {/* Image Upload */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-secondary">Upload Image</label>
                  <div className="upload-container p-3 bg-light rounded" style={{ border: '2px dashed #dee2e6' }}>
                    <div className="input-group">
                      <input
                        type="file"
                        className="form-control border-0 bg-white"
                        accept="image/*"
                        onChange={handleFileSelect}
                        disabled={uploadingFile}
                        style={{ borderRadius: '0.375rem 0 0 0.375rem' }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleImageUpload}
                        disabled={!selectedFile || uploadingFile}
                        style={{ 
                          borderRadius: '0 0.375rem 0.375rem 0',
                          background: 'linear-gradient(to right, #3b82f6, #2563eb)',
                          border: 'none'
                        }}
                      >
                        {uploadingFile ? (
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          <Upload size={16} />
                        )}
                      </button>
                    </div>
                    <p className="text-muted small mt-2 mb-0">
                      <i>Supported formats: JPG, PNG, GIF (Max 5MB)</i>
                    </p>
                  </div>
                </div>

                {/* Image Preview */}
                {previewImage && (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-bold text-secondary mb-0">Preview</label>
                      <span className="badge bg-info">New</span>
                    </div>
                    <div className="position-relative">
                      <img
                        src={previewImage}
                        alt="Preview"
                        className="img-fluid rounded shadow-sm"
                        style={{ 
                          maxHeight: "200px", 
                          width: "100%", 
                          objectFit: "cover",
                          borderRadius: '0.5rem'
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2 rounded-circle"
                        onClick={() => {
                          setSelectedFile(null);
                          setPreviewImage(null);
                        }}
                        style={{ width: '28px', height: '28px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Uploaded Images */}
                {formData.images && formData.images.length > 0 ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <label className="form-label fw-bold text-secondary mb-0">Gallery ({formData.images.length})</label>
                      {formData.images.length > 1 && (
                        <span className="badge bg-success">{formData.images.length} Images</span>
                      )}
                    </div>
                    <div className="row g-3">
                      {formData.images.map((image, index) => (
                        <div key={index} className="col-6">
                          <div className="position-relative h-100">
                            <img
                              src={`${assetPath}${image}`}
                              alt={`Accessory ${index + 1}`}
                              className="img-fluid rounded shadow-sm"
                              style={{ 
                                height: "100px", 
                                width: "100%", 
                                objectFit: "cover",
                                borderRadius: '0.5rem',
                                transition: 'transform 0.2s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.03)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle"
                              onClick={() => removeImage(index)}
                              disabled={uploadingFile}
                              style={{ width: '24px', height: '24px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              {uploadingFile ? (
                                <div className="spinner-border spinner-border-sm" role="status" style={{ width: '10px', height: '10px' }}>
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              ) : (
                                <Trash size={12} />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 bg-light rounded" style={{ border: '1px dashed #dee2e6' }}>
                    <Image size={40} className="text-muted mb-2" />
                    <p className="text-muted mb-0">No images uploaded yet</p>
                    <small className="text-muted">Upload images to showcase your accessory</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

Accessory.propTypes = {
  getAccessory: PropTypes.func.isRequired,
  addAccessory: PropTypes.func.isRequired,
  updateAccessory: PropTypes.func.isRequired,
  clearAccessory: PropTypes.func.isRequired,
  uploadAccessoryImage: PropTypes.func.isRequired,
  deleteAccessoryImage: PropTypes.func.isRequired,
  accessory: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  newAccessoryCreated: PropTypes.bool,
  assetPath: PropTypes.string
};

const mapStateToProps = (state) => ({
  accessory: state.accessory.accessory,
  loading: state.accessory.loading,
  newAccessoryCreated: state.accessory.newAccessoryCreated,
  assetPath: state.accessory.assetPath
});

export default connect(mapStateToProps, {
  getAccessory,
  addAccessory,
  updateAccessory,
  clearAccessory,
  uploadAccessoryImage,
  deleteAccessoryImage
})(Accessory); 